import { useState, useEffect, useCallback } from 'react';
import { Download } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { reportService } from '../../services/api';
import { Button, Skeleton, EmptyState, Toast, Tab, KPICard } from '../../components/ui';
import { thStyle, tdStyle } from '../../utils/tableStyles';
import { STATUS_NAMES, COLORS } from '../../constants';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'counsellor', label: 'Counsellor Performance' },
  { key: 'source', label: 'Lead Sources' },
  { key: 'course', label: 'Course-wise' },
];

const ReportsPage = () => {
  const { dark } = useTheme();
  const [tab, setTab] = useState('summary');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const fetch = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const params = { from: dateRange.from || undefined, to: dateRange.to || undefined };
      let resp;
      if (tab === 'summary') resp = await reportService.overall(params);
      else if (tab === 'counsellor') resp = await reportService.counsellorWise(params);
      else if (tab === 'source') resp = await reportService.leadSource(params);
      else resp = await reportService.courseWise(params);
      setData(resp.data.data);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error loading report', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab, dateRange]);

  useEffect(() => { fetch(); }, [fetch]);

  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16 };
  const inputStyle = { padding: '7px 10px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#F8FAFC', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' };

  const exportCSV = (rows, headers, filename) => {
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    if (!data) return;
    if (tab === 'summary') {
      exportCSV([['Total Leads', data.totalLeads], ['Converted', data.converted], ['Active', data.active], ['Conversion Rate', `${data.conversionRate ?? 0}%`]], ['Metric', 'Value'], 'report_summary.csv');
    } else if (Array.isArray(data)) {
      const keys = Object.keys(data[0] || {});
      exportCSV(data.map(r => keys.map(k => r[k])), keys, `report_${tab}.csv`);
    }
  };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Reports</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={dateRange.from} onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))} style={inputStyle} />
          <span style={{ color: dark ? '#64748B' : '#94A3B8', fontSize: 12 }}>to</span>
          <input type="date" value={dateRange.to} onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))} style={inputStyle} />
          <Button variant="outline" size="sm" icon={Download} onClick={handleExport} disabled={!data}>Export</Button>
        </div>
      </div>

      <div style={{ ...card, overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ padding: '16px 20px 0', borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <Tab tabs={TABS} active={tab} onChange={setTab} />
        </div>

        {loading ? (
          <div style={{ padding: 30 }}><Skeleton rows={6} cols={4} /></div>
        ) : !data ? (
          <div style={{ padding: 40 }}><EmptyState title="No report data" description="Select a date range and try again." /></div>
        ) : (
          <div style={{ padding: 24 }}>
            {tab === 'summary' && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
                  <KPICard label="Total Leads" value={data.totalLeads ?? 0} color="#3B82F6" />
                  <KPICard label="Converted" value={data.converted ?? 0} color="#10B981" />
                  <KPICard label="Active" value={data.active ?? 0} color="#8B5CF6" />
                  <KPICard label="Conversion Rate" value={`${data.conversionRate ?? 0}%`} color="#F59E0B" />
                </div>
                {data.byStatus && (
                  <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={Object.entries(data.byStatus).map(([code, count]) => ({ name: STATUS_NAMES[code] || code, count }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#1E293B' : '#F1F5F9'} />
                        <XAxis dataKey="name" tick={{ fill: dark ? '#64748B' : '#94A3B8', fontSize: 11 }} />
                        <YAxis tick={{ fill: dark ? '#64748B' : '#94A3B8', fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: dark ? '#1E293B' : '#fff', border: 'none', borderRadius: 8 }} />
                        <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {tab === 'counsellor' && Array.isArray(data) && (
              data.length === 0 ? <EmptyState title="No counsellor data" /> : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                      <tr>
                        <th style={thStyle(dark)}>Counsellor</th>
                        <th style={thStyle(dark)}>Total Leads</th>
                        <th style={thStyle(dark)}>Calls</th>
                        <th style={thStyle(dark)}>Follow-ups</th>
                        <th style={thStyle(dark)}>Converted</th>
                        <th style={thStyle(dark)}>Conv. Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                          <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{row.counsellor || row.name || '—'}</td>
                          <td style={tdStyle(dark)}>{row.totalLeads ?? row.leads ?? 0}</td>
                          <td style={tdStyle(dark)}>{row.totalCalls ?? row.calls ?? 0}</td>
                          <td style={tdStyle(dark)}>{row.totalFollowUps ?? row.followUps ?? 0}</td>
                          <td style={tdStyle(dark)}>{row.converted ?? 0}</td>
                          <td style={tdStyle(dark)}>{row.conversionRate ?? 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}

            {tab === 'source' && Array.isArray(data) && (
              data.length === 0 ? <EmptyState title="No lead source data" /> : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                  <div style={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: dark ? '#1E293B' : '#fff', border: 'none', borderRadius: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr><th style={thStyle(dark)}>Lead Source</th><th style={thStyle(dark)}>Count</th><th style={thStyle(dark)}>Converted</th></tr></thead>
                      <tbody>
                        {data.map((row, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                            <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{row.source || row.name}</td>
                            <td style={tdStyle(dark)}>{row.count}</td>
                            <td style={tdStyle(dark)}>{row.converted ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            )}

            {tab === 'course' && Array.isArray(data) && (
              data.length === 0 ? <EmptyState title="No course data" /> : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={thStyle(dark)}>Course</th><th style={thStyle(dark)}>Total Leads</th><th style={thStyle(dark)}>Converted</th><th style={thStyle(dark)}>Conv. Rate</th></tr></thead>
                    <tbody>
                      {data.map((row, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                          <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{row.course || row.name}</td>
                          <td style={tdStyle(dark)}>{row.totalLeads ?? row.leads ?? 0}</td>
                          <td style={tdStyle(dark)}>{row.converted ?? 0}</td>
                          <td style={tdStyle(dark)}>{row.conversionRate ?? 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
